import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Copy, Plus, RefreshCw, ArrowDownLeft, ArrowUpRight, ExternalLink, Send, QrCode, X, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { useWallets } from '../hooks/useData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Network configuration
const NETWORK_CONFIG = {
  ethereum: { color: '#627EEA', symbol: 'ETH', name: 'Ethereum', explorer: 'https://etherscan.io' },
  bitcoin: { color: '#F7931A', symbol: 'BTC', name: 'Bitcoin', explorer: 'https://blockchain.com/btc' },
  solana: { color: '#9945FF', symbol: 'SOL', name: 'Solana', explorer: 'https://solscan.io' },
  polygon: { color: '#8247E5', symbol: 'MATIC', name: 'Polygon', explorer: 'https://polygonscan.com' },
  bsc: { color: '#F0B90B', symbol: 'BNB', name: 'BSC', explorer: 'https://bscscan.com' },
  avalanche: { color: '#E84142', symbol: 'AVAX', name: 'Avalanche', explorer: 'https://snowtrace.io' },
  arbitrum: { color: '#28A0F0', symbol: 'ETH', name: 'Arbitrum', explorer: 'https://arbiscan.io' },
};

const Wallets = () => {
  // Use cached data for instant loading
  const { wallets, isLoading: loading, refresh: refreshWallets } = useWallets();
  const [showQR, setShowQR] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('ethereum');
  const [showSendModal, setShowSendModal] = useState(null);
  const [sendForm, setSendForm] = useState({ to: '', amount: '' });
  const [isSending, setIsSending] = useState(false);
  const [livePrices, setLivePrices] = useState({});

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('onerik_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch live prices from CoinGecko
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${API}/trading/prices/live`);
        const pricesArray = response.data.prices || [];
        const pricesObj = {};
        pricesArray.forEach(coin => {
          pricesObj[coin.symbol] = coin;
        });
        setLivePrices(pricesObj);
      } catch (error) {
        console.error('Error fetching prices:', error);
      }
    };
    
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  const generateWallet = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${API}/wallets/generate`,
        { network: selectedNetwork },
        { headers: getAuthHeader() }
      );
      toast.success(`${selectedNetwork.toUpperCase()} wallet generated!`);
      refreshWallets(); // Refresh the cached list
    } catch (error) {
      console.error('Error generating wallet:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate wallet');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Address copied to clipboard!');
  };

  const getNetworkConfig = (network) => {
    return NETWORK_CONFIG[network?.toLowerCase()] || {
      color: '#6B7280',
      symbol: network?.toUpperCase() || '?',
      name: network || 'Unknown',
      explorer: '#'
    };
  };

  const getQRValue = (wallet) => {
    const network = wallet.network?.toLowerCase();
    if (network === 'bitcoin') return `bitcoin:${wallet.address}`;
    if (network === 'solana') return `solana:${wallet.address}`;
    return `ethereum:${wallet.address}`;
  };

  const handleSend = async (wallet) => {
    if (!sendForm.to || !sendForm.amount) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsSending(true);
    try {
      const response = await axios.post(
        `${API}/wallets/send`,
        {
          from_address: wallet.address,
          to_address: sendForm.to,
          amount: sendForm.amount,
          network: wallet.network
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Transaction submitted! Hash: ${response.data.tx_hash?.slice(0, 10)}...`);
      setShowSendModal(null);
      setSendForm({ to: '', amount: '' });
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Transaction failed');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div data-testid="wallets-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Crypto Wallets</h1>
          <p className="text-white/60 text-sm md:text-base">Manage your multi-chain crypto wallets</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Generate New Wallet */}
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Plus className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold text-white">Generate New Wallet</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-2 block">Select Network</label>
                <select
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-xl h-12 px-4 text-white transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                >
                  {Object.entries(NETWORK_CONFIG).map(([key, config]) => (
                    <option key={key} value={key} className="bg-gray-900">
                      {config.name} ({config.symbol})
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={generateWallet}
                disabled={isGenerating}
                data-testid="generate-wallet-btn"
                className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Generate {NETWORK_CONFIG[selectedNetwork]?.symbol} Wallet
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Wallets List */}
          <div className="lg:col-span-2 bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Your Wallets ({wallets.length})</h2>
              <button
                onClick={refreshWallets}
                disabled={loading}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 text-white/60 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 text-primary mx-auto mb-4 animate-spin" />
                <p className="text-white/60">Loading wallets...</p>
              </div>
            ) : wallets.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/60 mb-2">No wallets found</p>
                <p className="text-white/40 text-sm">Generate your first wallet to get started</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {wallets.map((wallet, idx) => {
                  const config = getNetworkConfig(wallet.network);
                  const isQRVisible = showQR === wallet.address;

                  return (
                    <div
                      key={wallet.id || wallet.address || idx}
                      className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all"
                    >
                      {/* Wallet Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
                            style={{ backgroundColor: config.color + '30', color: config.color }}
                          >
                            {config.symbol.slice(0, 3)}
                          </div>
                          <div>
                            <div className="text-white font-medium">{config.name}</div>
                            <div className="text-sm text-white/50">{config.symbol} Wallet</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Live Price */}
                          {livePrices[config.symbol] && (
                            <div className="text-right">
                              <div className="text-white font-mono text-sm">
                                ${livePrices[config.symbol].price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <div className={`text-xs flex items-center justify-end gap-1 ${livePrices[config.symbol].change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {livePrices[config.symbol].change_24h >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {livePrices[config.symbol].change_24h >= 0 ? '+' : ''}{livePrices[config.symbol].change_24h?.toFixed(2)}%
                              </div>
                            </div>
                          )}
                          <a
                            href={`${config.explorer}/address/${wallet.address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="bg-black/50 rounded-lg p-3 mb-3">
                        <div className="text-xs text-white/40 mb-1">Wallet Address</div>
                        <div className="flex items-center gap-2">
                          <code className="text-sm text-white font-mono flex-1 break-all">
                            {wallet.address}
                          </code>
                          <button
                            onClick={() => copyToClipboard(wallet.address)}
                            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-primary"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* QR Code Toggle */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setShowQR(isQRVisible ? null : wallet.address)}
                          className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
                        >
                          <QrCode className="w-4 h-4" />
                          {isQRVisible ? 'Hide QR' : 'Receive'}
                        </button>
                        <button
                          onClick={() => setShowSendModal(wallet)}
                          className="text-sm text-green-400 hover:text-green-300 transition-colors flex items-center gap-2"
                        >
                          <Send className="w-4 h-4" />
                          Send
                        </button>
                      </div>

                      {/* QR Code */}
                      {isQRVisible && (
                        <div className="mt-4 flex justify-center bg-white rounded-xl p-4">
                          <QRCodeSVG
                            value={getQRValue(wallet)}
                            size={180}
                            level="H"
                            includeMargin={true}
                            bgColor="#ffffff"
                            fgColor="#000000"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black/90 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
                  style={{ 
                    backgroundColor: getNetworkConfig(showSendModal.network).color + '30', 
                    color: getNetworkConfig(showSendModal.network).color 
                  }}
                >
                  {getNetworkConfig(showSendModal.network).symbol.slice(0, 3)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Send {getNetworkConfig(showSendModal.network).symbol}</h3>
                  <p className="text-sm text-white/60">{getNetworkConfig(showSendModal.network).name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSendModal(null);
                  setSendForm({ to: '', amount: '' });
                }}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-2 block">From</label>
                <div className="bg-black/50 border border-white/10 rounded-xl p-3">
                  <code className="text-xs text-white/80 font-mono break-all">
                    {showSendModal.address}
                  </code>
                </div>
              </div>

              <div>
                <label className="text-sm text-white/60 mb-2 block">To Address</label>
                <input
                  type="text"
                  value={sendForm.to}
                  onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                  placeholder="Enter recipient address"
                  className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-xl h-12 px-4 text-white text-sm placeholder:text-white/30 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm text-white/60 mb-2 block">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={sendForm.amount}
                    onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })}
                    placeholder="0.00"
                    step="0.0001"
                    className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-xl h-12 px-4 pr-16 text-white text-sm placeholder:text-white/30 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 text-sm">
                    {getNetworkConfig(showSendModal.network).symbol}
                  </span>
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={() => handleSend(showSendModal)}
                  disabled={isSending || !sendForm.to || !sendForm.amount}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send {getNetworkConfig(showSendModal.network).symbol}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallets;
